import { useRouterState } from "@tanstack/react-router"
import { getSectionForPath, isHomePath } from "@/utils/home-sections"
import { routes } from "@/utils/routes"
import { HomePageSection } from "@/store/undoable-state"

export type RouteScene = {
	isHome: boolean
	isProjectRoute: boolean
	projectId: string | null
	homeSection: HomePageSection | null
	pathname: string
}

export const isProjectPath = (pathname: string) => pathname.startsWith(routes.project.pathPrefix)

export type StyleRouteScene = {
	isStyleDetailRoute: boolean
	styleId: string | null
	isLoading: boolean
	pathname: string
}

export const deriveRouteScene = (pathname: string): RouteScene => {
	const projectMatch = isProjectPath(pathname)
	const homeSection = getSectionForPath(pathname)
	const projectId = projectMatch
		? (pathname.slice(routes.project.pathPrefix.length).split("/").filter(Boolean)[0] ?? null)
		: null

	return {
		isHome: isHomePath(pathname),
		isProjectRoute: projectMatch,
		projectId,
		homeSection,
		pathname,
	}
}

export const useRouteScene = (): RouteScene => {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	})

	return deriveRouteScene(pathname)
}

export const useHomeSectionFromRouter = (): HomePageSection | null => {
	const scene = useRouteScene()
	return scene.homeSection
}

const STYLE_DETAIL_PATH_PREFIX = routes.styleDetail.pathPrefix

export const deriveStyleRouteScene = (pathname: string): StyleRouteScene => {
	if (pathname === routes.styleCreate.path) {
		return {
			isStyleDetailRoute: false,
			styleId: null,
			isLoading: false,
			pathname,
		}
	}

	if (!STYLE_DETAIL_PATH_PREFIX || !pathname.startsWith(STYLE_DETAIL_PATH_PREFIX)) {
		return {
			isStyleDetailRoute: false,
			styleId: null,
			isLoading: false,
			pathname,
		}
	}

	const remainder = pathname.slice(STYLE_DETAIL_PATH_PREFIX.length)
	const [encodedId] = remainder.split("/").filter(Boolean)
	const styleId = encodedId ? decodeURIComponent(encodedId) : null

	return {
		isStyleDetailRoute: Boolean(styleId),
		styleId,
		isLoading: false,
		pathname,
	}
}

export const useStyleRouteScene = (): StyleRouteScene => {
	const pathname = useRouterState({
		select: (state) => state.location.pathname,
	})

	return deriveStyleRouteScene(pathname)
}

export const useStyleRouteNavigation = () => {
	const navigateToStyles = routes.styles.useNavigate()
	const navigateToStyleDetail = routes.styleDetail.useNavigate()

	return {
		toList: (options?: { replace?: boolean; search?: unknown; hash?: string }) =>
			navigateToStyles(options),
		toDetail: (styleId: string) => navigateToStyleDetail({ styleId }),
	}
}
